import ContainerModalActions from "../../../../components/Containers/ContainerModalActions";
import {
  BUTTON_GO_BACK,
  BUTTON_APPLY_CHANGES,
  MODAL_PREVIEW_OPTIMIZATIONS_TITLE,
  MODAL_PREVIEW_OPTIMIZATIONS_DESCRIPTION,
  WHY_THIS_OPTIMIZATION,
  AD_GROUP,
  AD,
  CAMPAIGN,
  ASSETS_GROUP,
  EMPTY_RESULTS_SHOW,
  BUTTON_MARK_AS_DONE,
  COLUMN_NAME_FIELD,
  COLUMN_NAME_CURRENT_VALUE,
  COLUMN_NAME_NEW_VALUE,
  COLUMN_NAME_ACTION,
  COLUMN_NAME_ENTITY,
  DELETED,
  YES,
  NO,
  NEW,
  BUTTON_DISCARD,
  DISCARD_OPTIMIZATION_ALERT_TITLE,
  DISCARD_OPTIMIZATION_ALERT_DESCRIPTION,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import ModalBasicLayout from "../../../../components/Modal/ModalBasicLayout";
import Accordion from "../../../../components/Containers/Accordion";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableContainer from "../../../../components/Tables/TableContainer";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import IconButton from "../../../../components/Buttons/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { capitalizeFirstLetter } from "../../../../utils/string";
import _ from "lodash";
import {
  ADS_ACTION_TYPES,
  ADS_KEYS_CREATIVES,
  ADS_KEYS_TEXT,
  ADS_OBJECT_TYPES,
  PLAN_TYPE_CREDITS,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import ModalInsightReasoning from "./ModalInsightReasoning";
import { getAdsCampaignsSchemasKey } from "../../../../services/adsCampaigns";
import useApplyOptimizationsCampaign from "../../../../hooks/useApplyOptimizationsCampaign";
import { getUniqueID } from "../../../../utils/numbers";
import {
  startSaveOptimizationInLog,
  startVerifyAndSetAdsUsage,
} from "../../../../actions/adsCampaigns";
import i18next from "i18next";
import ScheduleDisplay from "./PreviewOptimizationFields/ScheduleDisplay";
import { ConfirmAlert } from "../../../../components/Alerts/Alerts";
import { getIsBlockedActions } from "../../../../actions/getters";

const KEYS_IGNORED_TO_SHOW = [
  "id",
  "country_code",
  "country_name",
  "region",
  "region_id",
  "supports_city",
  "supports_region",
  "type",
];

const EMPTY_VALUE = "-";

// New structure constants
const OPERATION_TYPES = {
  ADD: "add",
  REMOVE: "remove",
  UPDATE: "update",
  CREATE: "create",
};

const MAP_TRANSLATIONS_ENTITIES = {
  [ADS_OBJECT_TYPES.adGroup]: AD_GROUP,
  [ADS_OBJECT_TYPES.assetsGroup]: ASSETS_GROUP,
  [ADS_OBJECT_TYPES.campaign]: CAMPAIGN,
  [ADS_OBJECT_TYPES.ad]: AD,
};

const sxCutText = (maxLines = 2) => ({
  whiteSpace: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: maxLines,
  WebkitBoxOrient: "vertical",
});

const getKeyValuePairs = (item, schemaKeys) => {
  if (!_.isArray(item) && _.isString(item)) {
    return [item];
  }
  const filteredEntries = Object.entries(item).filter(
    ([key]) => schemaKeys.includes(key) && !KEYS_IGNORED_TO_SHOW.includes(key)
  );
  let keyValuePairs;
  if (filteredEntries.length === 1) {
    // Only one value, show just the value
    keyValuePairs = [String(filteredEntries[0][1])];
  } else if (filteredEntries.length > 1) {
    // More than one, show key: value
    keyValuePairs = filteredEntries.map(([key, value]) => `${key}: ${value}`);
  } else {
    keyValuePairs = [];
  }
  return keyValuePairs;
};

// Helper functions based on useApplyOptimizationsCampaign
const isValidJSON = (value) => {
  if (typeof value !== "string") return false;
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    return false;
  }
};

const parseValueSafely = (value, onlyObjects = false) => {
  if (isValidJSON(value)) {
    try {
      const parsedValue = JSON.parse(value);
      if (onlyObjects && !_.isObject(parsedValue) && !_.isArray(parsedValue)) {
        return value;
      }
      return parsedValue;
    } catch (error) {
      return value;
    }
  }
  return value;
};

// Function to check if value should be expanded into multiple rows
const shouldExpandValue = (value) => {
  return _.isArray(value) && value.length > 1;
};

// Function to expand array values into individual change objects
const expandChangesWithArrays = (changes) => {
  const expandedChanges = [];

  changes.forEach((change, originalIndex) => {
    let value = parseValueSafely(change.value);

    // For REMOVE operations, check itemsByValue instead of value
    if (change.operation === OPERATION_TYPES.REMOVE && change.itemsByValue) {
      value = change.itemsByValue;
    } else if (
      change.operation === OPERATION_TYPES.REMOVE &&
      change.itemsByID
    ) {
      // For REMOVE by ID, expand using the IDs list
      value = change.itemsByID;
    }

    // If value is array and has multiple items, create separate row for each
    if (shouldExpandValue(value)) {
      value.forEach((item, itemIndex) => {
        const expandedChange = {
          ...change,
          value: item,
          _isExpanded: true,
          _originalIndex: originalIndex,
          _itemIndex: itemIndex,
          _totalItems: value.length,
          // Unique key for React
          _expandedKey: `${originalIndex}-${itemIndex}`,
        };
        expandedChanges.push(expandedChange);
      });
    } else {
      // Single value or simple change
      expandedChanges.push({
        ...change,
        _isExpanded: false,
        _originalIndex: originalIndex,
        _expandedKey: `${originalIndex}-single`,
      });
    }
  });

  return expandedChanges;
};

// Helper function to get current value from campaign data
const getCurrentValue = (change, getValues) => {
  if (
    change.operation === OPERATION_TYPES.ADD ||
    change.operation === OPERATION_TYPES.CREATE
  ) {
    return i18next.t(NEW); // Adding new value, so show "NEW" instead of empty
  }

  if (change.operation === OPERATION_TYPES.REMOVE) {
    // Prefer itemsByValue if present
    if (Array.isArray(change.itemsByValue)) {
      if (change._isExpanded && typeof change._itemIndex === "number") {
        return change.itemsByValue[change._itemIndex] || EMPTY_VALUE;
      }
      return change.itemsByValue.join(", ");
    }
    // Handle itemsByID: map IDs to names if possible
    if (Array.isArray(change.itemsByID)) {
      const campaignData = getValues();

      // Locate the source array in form data for this field to resolve IDs to display names
      const resolveNameById = (id) => {
        // Build base reference to the array for the field
        let container;
        if (change.entity === ADS_OBJECT_TYPES.campaign) {
          container =
            campaignData?.platformsData?.[change.platform]?.[change.field];
        } else if (change.entity === ADS_OBJECT_TYPES.assetsGroup) {
          const groups = campaignData?.assetsGroups || [];
          const idx = groups.findIndex(
            (g) =>
              g.id === change.adGroupID || g.idPlatform === change.adGroupID
          );
          container = idx >= 0 ? groups[idx]?.[change.field] : undefined;
        } else if (change.entity === ADS_OBJECT_TYPES.adGroup) {
          const groups = campaignData?.adsGroups || [];
          const idx = groups.findIndex(
            (g) =>
              g.id === change.adGroupID || g.idPlatform === change.adGroupID
          );
          container = idx >= 0 ? groups[idx]?.[change.field] : undefined;
        } else if (change.entity === ADS_OBJECT_TYPES.ad) {
          const groups = campaignData?.adsGroups || [];
          const gIdx = groups.findIndex(
            (g) =>
              g.id === change.adGroupID || g.idPlatform === change.adGroupID
          );
          if (gIdx >= 0) {
            const ads = groups[gIdx]?.ads || [];
            const aIdx = ads.findIndex(
              (ad) => ad.id === change.adID || ad.idPlatform === change.adID
            );
            container = aIdx >= 0 ? ads[aIdx]?.[change.field] : undefined;
          }
        }

        // If container is array of objects, try finding by id/idPlatform
        if (Array.isArray(container)) {
          const found = container.find(
            (it) => it?.id === id || it?.idPlatform === id || it?.value === id
          );
          // Prefer common name keys
          return (
            found?.name || found?.value || found?.label || found?.title || id
          );
        }
        return id;
      };

      if (change._isExpanded && typeof change._itemIndex === "number") {
        const id = change.itemsByID[change._itemIndex];
        return resolveNameById(id) || EMPTY_VALUE;
      }
      // Not expanded: join resolved names
      return change.itemsByID.map((id) => resolveNameById(id)).join(", ");
    }
  }

  // Try to get current value from form data
  try {
    const campaignData = getValues();

    // Build path dynamically based on entity type
    const pathSegments = [];

    if (change.entity === ADS_OBJECT_TYPES.campaign) {
      if (change.field === "status") {
        pathSegments.push("states");
        pathSegments.push(change.platform);
      } else {
        // For campaign entity, access platformsData root level
        pathSegments.push("platformsData");
        pathSegments.push(change.platform);
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.assetsGroup) {
      pathSegments.push("assetsGroups");
      // Find assetsGroup by ID (could be id or idPlatform)
      const assetsGroups = campaignData.assetsGroups || [];
      const groupIndex = assetsGroups.findIndex(
        (group) =>
          group.id === change.adGroupID || group.idPlatform === change.adGroupID
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.adGroup) {
      pathSegments.push("adsGroups");
      // Find adGroup by ID (could be id or idPlatform)
      const adsGroups = campaignData.adsGroups || [];
      const groupIndex = adsGroups.findIndex(
        (group) =>
          group.id === change.adGroupID || group.idPlatform === change.adGroupID
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push(change.field);
      }
    } else if (change.entity === ADS_OBJECT_TYPES.ad) {
      pathSegments.push("adsGroups");
      // Find adGroup by ID
      const adsGroups = campaignData.adsGroups || [];
      const groupIndex = adsGroups.findIndex(
        (group) =>
          group.id === change.adGroupID || group.idPlatform === change.adGroupID
      );
      if (groupIndex >= 0) {
        pathSegments.push(groupIndex.toString());
        pathSegments.push("ads");
        // Find ad by ID
        const ads = adsGroups[groupIndex].ads || [];
        const adIndex = ads.findIndex(
          (ad) => ad.id === change.adID || ad.idPlatform === change.adID
        );
        if (adIndex >= 0) {
          pathSegments.push(adIndex.toString());
          pathSegments.push(change.field);
        }
      }
    }

    // Navigate to the value
    let currentValue = campaignData;
    for (const segment of pathSegments) {
      if (currentValue && typeof currentValue === "object") {
        currentValue = currentValue[segment];
      } else {
        currentValue = undefined;
        break;
      }
    }

    // Format the current value based on field type
    if (currentValue !== undefined && currentValue !== null) {
      // Handle text fields that are arrays of objects {value, id}
      if (ADS_KEYS_TEXT.includes(change.field) && Array.isArray(currentValue)) {
        const values = currentValue
          .map((item) => (typeof item === "object" ? item.value : item))
          .filter(Boolean);
        // If this is an expanded row, return only the specific item
        if (change._isExpanded && typeof change._itemIndex === "number") {
          return values[change._itemIndex] || EMPTY_VALUE;
        }

        // If not expanded, return all values joined
        return values.length > 0 ? values : EMPTY_VALUE;
      }

      // Handle regular arrays
      if (Array.isArray(currentValue)) {
        // If this is an expanded row, return only the specific item
        if (change._isExpanded && typeof change._itemIndex === "number") {
          return currentValue[change._itemIndex] || EMPTY_VALUE;
        }

        // If not expanded, return all values joined
        return currentValue.length > 0 ? currentValue : EMPTY_VALUE;
      }

      if (typeof currentValue === "object") {
        return currentValue;
      }

      if (_.isArray(currentValue)) {
        return currentValue;
      }

      return String(currentValue);
    }
  } catch (error) {
    console.warn("Error getting current value:", error);
  }

  // Fallback placeholders based on field type
  if (ADS_KEYS_TEXT.includes(change.field)) {
    return "Current text content";
  }

  switch (change.field) {
    case "budget":
      return "";
    default:
      return "";
  }
};

const ModalPreviewOptimization = ({
  modalOpen,
  insight,
  insightIndex,
  onCloseModal = () => {},
  onCallbackApplyOptimization = () => {},
  onCallbackDiscardOptimization = () => {},
}) => {
  const { t } = useTranslationApp();
  const { ADS_CAMPAIGN_CHAT_BOT_ACTION } = useCreditActionCosts();
  // Using changes array for new structure
  const [isLoadingChanges, setIsLoadingChanges] = useState(true);
  const [changes, setChanges] = useState([]);
  const methods = useFormContext();
  const { campaignID, getValues } = methods;
  const [modalReasoningOpen, setModalReasoningOpen] = useState(false);

  const { applyOptimizations } = useApplyOptimizationsCampaign({
    methods,
    campaignID,
  });

  const schemas = useSelectorApp((state) => state?.adsCampaigns?.schemas);
  const typeOfPlans = useSelectorApp((state) => state?.payments?.typeOfPlans);

  const platform = insight.platform;
  const objective = getValues(`platformsData.${platform}.objective`);
  const schemaKey = getAdsCampaignsSchemasKey({ platform, objective });
  const schema = schemas?.[schemaKey];

  const isInCompatibleCampaign = getValues(
    `platformsData.${platform}.isIncompatible`
  );

  const isExternalOptimization = insight.isExternal;
  const isIncompatibleCampaignMode =
    isInCompatibleCampaign && !isExternalOptimization;
  const isCompatibleMode = !isExternalOptimization && !isInCompatibleCampaign;
  const isOptimizationUnapplicable =
    isExternalOptimization || isInCompatibleCampaign;

  const isRiskyOptimization =
    isCompatibleMode && insight.isRisky && !isExternalOptimization;

  const hasPreviewChanges =
    changes.length > 0 ||
    (Array.isArray(insight.changes) && insight.changes.length > 0);

  const isBigModal = !isExternalOptimization;
  const showTable = isBigModal && hasPreviewChanges;
  const showReasoning = isBigModal && insight.reasoning && !isRiskyOptimization;
  const useMarkAsDonePrimary = isOptimizationUnapplicable;

  const dispatch = useDispatchApp();

  const onMarkOptimizationAsDone = async () => {
    const newOptimization = {
      ...insight,
      changes:
        Array.isArray(insight.changes) && insight.changes.length > 0
          ? insight.changes
          : changes,
    };

    dispatch(
      startSaveOptimizationInLog({
        campaignID,
        platform,
        optimization: { ...newOptimization, isApplied: true },
      })
    );

    onCloseModal();
    onCallbackApplyOptimization({
      optimization: newOptimization,
      optimizationIndex: insightIndex,
    });
  };

  const onApplyOptimizations = async (changesProp = changes) => {
    const newOptimization = {
      ...insight,
      changes: changesProp,
    };

    if (isOptimizationUnapplicable) {
      await onMarkOptimizationAsDone();
      return;
    }

    if (!isOptimizationUnapplicable) {
      if (typeOfPlans === PLAN_TYPE_CREDITS) {
        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_CHAT_BOT_ACTION,
            showUsageConfirmationAlert: true,
          })
        );
        if (isBlocked) {
          return;
        }
      }

      const isApplied = applyOptimizations([newOptimization]);
      if (!isApplied) return;

      if (typeOfPlans === PLAN_TYPE_CREDITS) {
        dispatch(
          startVerifyAndSetAdsUsage({
            actionID: ADS_ACTION_TYPES.MESSAGES,
          })
        );
      }
    }

    dispatch(
      startSaveOptimizationInLog({
        campaignID,
        platform,
        optimization: { ...newOptimization, isApplied: true },
      })
    );

    onCloseModal();
    onCallbackApplyOptimization({
      optimization: newOptimization,
      optimizationIndex: insightIndex,
    });
  };

  const onDiscardOptimization = async () => {
    const result = await ConfirmAlert({
      title: t(DISCARD_OPTIMIZATION_ALERT_TITLE),
      text: t(DISCARD_OPTIMIZATION_ALERT_DESCRIPTION),
    });

    if (result.isConfirmed) {
      if (onCallbackDiscardOptimization) {
        onCallbackDiscardOptimization({
          optimization: insight,
          optimizationIndex: insightIndex,
        });
        onCloseModal();
      }
    }
  };

  const onDeleteChange = (changeToDelete) => {
    if (changeToDelete._isExpanded) {
      // This is an expanded item - need to remove it from the original array
      const originalChangeIndex = changeToDelete._originalIndex;
      const itemIndex = changeToDelete._itemIndex;

      // Get the original change
      const originalChange = changes[originalChangeIndex];
      if (!originalChange) return;
      // Prepare updated changes
      const updatedChanges = [...changes];

      // If operation is REMOVE, the items come from itemsByValue/itemsByID
      if (
        originalChange.operation === OPERATION_TYPES.REMOVE &&
        Array.isArray(originalChange.itemsByValue)
      ) {
        const updatedItems = originalChange.itemsByValue.filter(
          (_, index) => index !== itemIndex
        );

        if (updatedItems.length === 0) {
          // No items left, remove the change entirely
          updatedChanges.splice(originalChangeIndex, 1);
        } else {
          updatedChanges[originalChangeIndex] = {
            ...originalChange,
            itemsByValue: updatedItems,
          };
        }

        setChanges(updatedChanges);
        return;
      }

      if (
        originalChange.operation === OPERATION_TYPES.REMOVE &&
        Array.isArray(originalChange.itemsByID)
      ) {
        const updatedIDs = originalChange.itemsByID.filter(
          (_, index) => index !== itemIndex
        );

        if (updatedIDs.length === 0) {
          updatedChanges.splice(originalChangeIndex, 1);
        } else {
          updatedChanges[originalChangeIndex] = {
            ...originalChange,
            itemsByID: updatedIDs,
          };
        }

        setChanges(updatedChanges);
        return;
      }

      // Fallback: handle arrays that live in value (ADD/UPDATE or other cases)
      let originalArray = parseValueSafely(originalChange.value);
      if (!Array.isArray(originalArray)) return;

      // Remove the specific item
      const updatedArray = originalArray.filter(
        (_, index) => index !== itemIndex
      );

      if (updatedArray.length === 0) {
        // If array is empty, remove the entire change
        updatedChanges.splice(originalChangeIndex, 1);
      } else {
        // Update the original change with the new array
        updatedChanges[originalChangeIndex] = {
          ...originalChange,
          value: JSON.stringify(updatedArray),
        };
      }

      setChanges(updatedChanges);
    } else {
      // This is a single change - remove it entirely
      const updatedChanges = changes.filter(
        (_, index) => index !== changeToDelete._originalIndex
      );
      setChanges(updatedChanges);
    }
  };

  const getInitialData = async () => {
    if (insight.changes) {
      const isOptimizationCreative = insight.changes.some((change) =>
        ADS_KEYS_CREATIVES.includes(change.field)
      );
      if (isOptimizationCreative && !isOptimizationUnapplicable) {
        onApplyOptimizations(insight.changes);
        onCloseModal();
        return;
      }
    }

    setIsLoadingChanges(false);

    if (
      !isExternalOptimization &&
      insight.changes &&
      Array.isArray(insight.changes)
    ) {
      setChanges(insight.changes);
    }
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, []);

  if (isLoadingChanges) {
    return null;
  }

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth={isBigModal ? "lg" : "sm"}
      title={t(MODAL_PREVIEW_OPTIMIZATIONS_TITLE)}
      description={t(MODAL_PREVIEW_OPTIMIZATIONS_DESCRIPTION)}
    >
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {insight.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxHeight: 100,
              overflowY: "auto",
              pr: 1,
              mb: 1,
            }}
          >
            {insight.action}
          </Typography>
          {showReasoning && (
            <Accordion
              title={t(WHY_THIS_OPTIMIZATION)}
              variantTitle="body1"
              sx={{
                backgroundColor: "#f5f5f5",
                "::before": { backgroundColor: "transparent" },
                m: "0 !important",
                mb: 1,
              }}
              sxTitle={{ fontSize: "0.95rem" }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  maxHeight: 100,
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                {insight.reasoning}
              </Typography>
            </Accordion>
          )}
          {showTable && (
            <TableChanges
              changes={changes}
              schema={schema}
              onDeleteChange={onDeleteChange}
              readOnly={isIncompatibleCampaignMode}
            />
          )}
        </Box>
      </Box>
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
          propsTertiaryButton={{
            showButton: true,
            onClick: onDiscardOptimization,
            text: BUTTON_DISCARD,
          }}
          propsPrimaryButton={
            useMarkAsDonePrimary
              ? {
                  showButton: true,
                  text: BUTTON_MARK_AS_DONE,
                  type: "submit",
                  onClick: onMarkOptimizationAsDone,
                }
              : {
                  showButton: true,
                  text: BUTTON_APPLY_CHANGES,
                  disabled: changes.length === 0,
                  type: "submit",
                  onClick: () => {
                    onApplyOptimizations();
                  },
                }
          }
          sxButtonPrimary={
            useMarkAsDonePrimary
              ? {
                  color: "primary",
                }
              : {}
          }
        />
      </DialogActions>
      {modalReasoningOpen && (
        <ModalInsightReasoning
          modalOpen={modalReasoningOpen}
          onCloseModal={() => setModalReasoningOpen(false)}
          insight={insight}
        />
      )}
    </ModalBasicLayout>
  );
};

// Enhanced table component for displaying changes in new structure - similar to TableModifications
const TableChanges = ({
  changes = [],
  schema = null,
  onDeleteChange = () => {},
  readOnly = false,
}) => {
  const { t } = useTranslationApp();
  const { getValues } = useFormContext();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  // Expand changes with arrays into individual rows
  const expandedChanges = expandChangesWithArrays(changes);

  if (expandedChanges.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography>{t(EMPTY_RESULTS_SHOW)}</Typography>
      </Box>
    );
  }

  // Column configuration based on screen size
  const getColumnConfig = () => {
    const showActionColumn = !readOnly;

    if (isMobile) {
      return {
        entity: { width: "25%", show: true },
        field: { width: "25%", show: true },
        currentValue: { width: "25%", show: false }, // Hidden on mobile
        newValue: { width: "25%", show: true },
        action: { width: "10%", show: showActionColumn },
      };
    }

    if (isTablet) {
      return {
        entity: { width: "20%", show: true },
        field: { width: "15%", show: true },
        currentValue: { width: "25%", show: true },
        newValue: { width: "25%", show: true },
        action: { width: "10%", show: showActionColumn },
      };
    }

    // Desktop
    return {
      entity: { width: "18%", show: true },
      field: { width: "12%", show: true },
      currentValue: { width: "25%", show: true },
      newValue: { width: "25%", show: true },
      action: { width: "10%", show: showActionColumn },
    };
  };

  const columnConfig = getColumnConfig();

  // Helper function to get entity display data with structured format
  const getEntityDisplayData = (change) => {
    try {
      if (change.operation === OPERATION_TYPES.CREATE) {
        if (
          change.entity === ADS_OBJECT_TYPES.adGroup ||
          change.entity === ADS_OBJECT_TYPES.assetsGroup
        ) {
          const newValue = parseValueSafely(change.value);
          const adGroupName = newValue.name || "";
          return {
            type: change.entity,
            adGroupLabel: t(
              change.entity === ADS_OBJECT_TYPES.adGroup
                ? AD_GROUP
                : ASSETS_GROUP
            ),
            adGroupName: adGroupName,
            adLabel: "",
            adName: "",
          };
        }
      }
      // Handle CAMPAIGN entity - uses platformsData
      if (change.entity === ADS_OBJECT_TYPES.campaign) {
        return {
          type: "campaign",
          adGroupLabel: t(CAMPAIGN),
          adGroupName: "",
          adLabel: "",
          adName: "",
        };
      }

      // Handle ASSETS_GROUP entity - similar to adsGroups
      if (change.entity === ADS_OBJECT_TYPES.assetsGroup) {
        const campaignData = getValues();
        const assetsGroups = campaignData.assetsGroups || [];

        const assetsGroup = assetsGroups.find(
          (group) =>
            group.id === change.assetsGroupID ||
            group.idPlatform === change.assetsGroupID
        );

        return {
          type: "assetsGroup",
          adGroupLabel: t(ASSETS_GROUP),
          adGroupName: assetsGroup?.name || "",
          adLabel: "",
          adName: "",
        };
      }

      // Handle AD_GROUP and AD entities - original logic
      const campaignData = getValues();
      const adsGroups = campaignData.adsGroups || [];

      // Find the ad group
      const adGroup = adsGroups.find(
        (group) =>
          group.id === change.adGroupID || group.idPlatform === change.adGroupID
      );

      if (!adGroup) {
        return {
          type: change.entity === ADS_OBJECT_TYPES.ad ? "ad" : "adGroup",
          adGroupLabel: t(AD_GROUP),
          adGroupName: "",
          adLabel: change.entity === ADS_OBJECT_TYPES.ad ? t(AD) : "",
          adName: "",
        };
      }

      if (change.entity === ADS_OBJECT_TYPES.adGroup) {
        return {
          type: "adGroup",
          adGroupLabel: t(AD_GROUP),
          adGroupName: adGroup.name || "",
          adLabel: "",
          adName: "",
        };
      }

      if (change.entity === ADS_OBJECT_TYPES.ad) {
        if (change.operation === OPERATION_TYPES.CREATE) {
          const newValue = parseValueSafely(change.value);
          const adName = newValue.name || "";
          return {
            type: "ad",
            adGroupLabel: t(AD_GROUP),
            adGroupName: adGroup.name || "",
            adLabel: t(AD),
            adName: adName,
          };
        }

        if (change.adID) {
          // Find the ad within the ad group
          const ad = (adGroup.ads || []).find(
            (ad) => ad.id === change.adID || ad.idPlatform === change.adID
          );

          return {
            type: "ad",
            adGroupLabel: t(AD_GROUP),
            adGroupName: adGroup.name || "",
            adLabel: t(AD),
            adName: ad?.name || "",
          };
        }
      }

      return {
        type: change.entity === ADS_OBJECT_TYPES.ad ? "ad" : "adGroup",
        adGroupLabel: t(AD_GROUP),
        adGroupName: "",
        adLabel: change.entity === ADS_OBJECT_TYPES.ad ? t(AD) : "",
        adName: "",
      };
    } catch (error) {
      console.warn("Error getting entity display data:", error);
      return {
        type: change.entity || "unknown",
        adGroupLabel: t(AD_GROUP),
        adGroupName: "",
        adLabel: change.entity === ADS_OBJECT_TYPES.ad ? t(AD) : "",
        adName: "",
      };
    }
  };

  // Helper function to get field display name
  const getFieldDisplayName = (change) => {
    if (change.operation === OPERATION_TYPES.CREATE) {
      return t(MAP_TRANSLATIONS_ENTITIES[change.entity]);
    }
    return capitalizeFirstLetter(change.field);
  };

  // Simple header cell without tooltips
  const HeaderCell = ({ title, width, show = true }) => {
    if (!show) return null;

    return (
      <TableCell
        isHeader={true}
        sx={{
          width: width,
          minWidth: isMobile ? "auto" : width === "35%" ? 250 : "auto",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </TableCell>
    );
  };

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        keys={expandedChanges}
        hasPagination={false}
        hasMarginBottom={false}
      >
        <TableHead>
          <TableRow>
            <HeaderCell
              title={t(COLUMN_NAME_ENTITY)}
              width={columnConfig.entity.width}
              show={columnConfig.entity.show}
            />
            <HeaderCell
              title={t(COLUMN_NAME_FIELD)}
              width={columnConfig.field.width}
              show={columnConfig.field.show}
            />
            <HeaderCell
              title={t(COLUMN_NAME_CURRENT_VALUE)}
              width={columnConfig.currentValue.width}
              show={columnConfig.currentValue.show}
            />
            <HeaderCell
              title={t(COLUMN_NAME_NEW_VALUE)}
              width={columnConfig.newValue.width}
              show={columnConfig.newValue.show}
            />
            <HeaderCell
              title={t(COLUMN_NAME_ACTION)}
              width={columnConfig.action.width}
              show={columnConfig.action.show}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {expandedChanges.map((change, index) => {
            const isLastRow = index === expandedChanges.length - 1;

            return (
              <TableRow
                key={change._expandedKey}
                sx={{
                  backgroundColor: change._isExpanded ? "#f8f9ff" : "inherit",
                  borderLeft: change._isExpanded ? "3px solid #e3f2fd" : "none",
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: change._isExpanded ? "#f0f4ff" : "#f5f5f5",
                  },
                }}
              >
                {/* Entity Column */}
                {columnConfig.entity.show && (
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    showTooltip={false}
                    sx={{
                      width: columnConfig.entity.width,
                      maxWidth: isMobile ? "none" : 200,
                    }}
                  >
                    {(() => {
                      const entityData = getEntityDisplayData(change);
                      return (
                        <Box>
                          {/* Ad Group Line */}
                          <Typography
                            variant={"body2"}
                            sx={{
                              wordBreak: "break-word",
                              ...sxCutText(2),
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {entityData.adGroupLabel}:
                            </Box>
                            {entityData.adGroupName && (
                              <Box component="span" sx={{ ml: 0.5 }}>
                                {entityData.adGroupName}
                              </Box>
                            )}
                          </Typography>

                          {/* Ad Line (only for ads) */}
                          {entityData.type === "ad" && (
                            <Typography
                              variant={"body2"}
                              sx={{
                                wordBreak: "break-word",
                                ...sxCutText(2),
                              }}
                            >
                              <Box component="span" sx={{ fontWeight: 600 }}>
                                {entityData.adLabel}:
                              </Box>
                              {entityData.adName && (
                                <Box component="span" sx={{ ml: 0.5 }}>
                                  {entityData.adName}
                                </Box>
                              )}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                )}

                {/* Field Column */}
                {columnConfig.field.show && (
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    showTooltip={false}
                    sx={{
                      width: columnConfig.field.width,
                      maxWidth: isMobile ? "none" : 150,
                    }}
                  >
                    <Typography
                      variant={"body2"}
                      sx={{
                        wordBreak: "break-word",
                        ...sxCutText(2),
                      }}
                    >
                      {getFieldDisplayName(change)}
                    </Typography>
                  </TableCell>
                )}

                {/* Current Value Column */}
                {columnConfig.currentValue.show && (
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    showTooltip={false}
                    sx={{
                      width: columnConfig.currentValue.width,
                      maxWidth: isMobile ? "none" : 300,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <ValueDisplaySmart
                      change={change}
                      from="current"
                      schema={schema}
                    />
                  </TableCell>
                )}

                {/* New Value Column */}
                {columnConfig.newValue.show && (
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    showTooltip={false}
                    sx={{
                      width: columnConfig.newValue.width,
                      minWidth: isMobile ? "none" : 250,
                      position: "relative",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <ValueDisplaySmart
                      change={change}
                      from="new"
                      schema={schema}
                    />
                  </TableCell>
                )}

                {/* Action Column */}
                {columnConfig.action.show && (
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    sx={{
                      width: columnConfig.action.width,
                    }}
                  >
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: "error.main",
                      }}
                      onClick={() => onDeleteChange(change)}
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

// Super intelligent and dynamic component to display values based on schema
const ValueDisplaySmart = ({ change, from, schema }) => {
  const { getValues } = useFormContext();
  const { t } = useTranslationApp();

  // Handle REMOVE operations first
  if (change.operation === OPERATION_TYPES.REMOVE && from === "new") {
    return <Typography variant="body2">{t(DELETED)}</Typography>;
  }

  // Get the raw value - for new values, get the original value from change
  let rawValue =
    from === "new" ? change.value : getCurrentValue(change, getValues);

  // For expanded items, we need to get the individual item value
  if (change._isExpanded && from === "new") {
    // Parse the original array and get the specific item
    const arrayValue = parseValueSafely(change.value);
    if (Array.isArray(arrayValue) && typeof change._itemIndex === "number") {
      rawValue = arrayValue[change._itemIndex];
    }
  }

  if (change.operation === OPERATION_TYPES.CREATE && from === "new") {
    const parsedValue = parseValueSafely(rawValue);
    return (
      <Typography
        variant="body2"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {String(parsedValue?.name || EMPTY_VALUE)}
      </Typography>
    );
  }

  // Get field schema if available
  const fieldSchema = schema?.[change.entity]?.[change.field];
  // If no schema available, fallback to simple display
  if (!fieldSchema) {
    return (
      <Typography
        variant="body2"
        sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
      >
        {String(rawValue || EMPTY_VALUE)}
      </Typography>
    );
  }

  // Handle different field types based on schema
  const renderValueByType = () => {
    // Special handling for schedule field
    if (change.field === "schedule") {
      return (
        <ScheduleDisplay scheduleValue={rawValue} emptyValue={EMPTY_VALUE} />
      );
    }

    switch (fieldSchema.type) {
      case "string":
        return (
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
            }}
          >
            {String(rawValue || EMPTY_VALUE)}
          </Typography>
        );

      case "array": {
        // Parse the value safely using the existing helper
        let arrayValue = parseValueSafely(rawValue);
        if (!Array.isArray(arrayValue)) {
          arrayValue = [arrayValue];
        }

        // Check if this is an array of objects with schema
        const isArrayOfObjects =
          fieldSchema.elementType === "object" && fieldSchema.schema;

        if (isArrayOfObjects) {
          // For arrays of objects, extract key-value pairs based on schema
          const displayValues = arrayValue.map((item) => {
            if (typeof item === "object" && item !== null) {
              // Get key-value pairs only for keys defined in schema, excluding 'id'
              const schemaKeys = Object.keys(fieldSchema.schema || {});
              let keyValuePairs = getKeyValuePairs(item, schemaKeys);

              return (
                <Box sx={{ position: "relative" }} key={getUniqueID()}>
                  {keyValuePairs.map((pair, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {pair}
                    </Typography>
                  ))}
                </Box>
              );
            }

            return (
              <Typography variant="body2" key={getUniqueID()}>
                {String(item || EMPTY_VALUE)}
              </Typography>
            );
          });
          // For expanded items, show key-value pairs based on schema
          if (change._isExpanded) {
            const itemValue = rawValue;
            if (itemValue === EMPTY_VALUE) {
              return <Typography variant="body2">{EMPTY_VALUE}</Typography>;
            }
            const schemaKeys = Object.keys(fieldSchema.schema || {});
            const keyValuePairs = getKeyValuePairs(itemValue, schemaKeys);
            return (
              <Box sx={{ position: "relative" }}>
                {keyValuePairs.map((pair, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {pair}
                  </Typography>
                ))}
              </Box>
            );
          }

          // For non-expanded arrays of objects, show count and names
          return <Box>{displayValues}</Box>;
        }

        // For regular arrays (strings, numbers, etc.)
        // For expanded items, show just the individual value
        if (change._isExpanded) {
          return (
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="body2"
                sx={{
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {String(rawValue || EMPTY_VALUE)}
              </Typography>
            </Box>
          );
        }

        // For non-expanded regular arrays, show count and preview
        return (
          <Box>
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
            >
              {String(arrayValue[0] || EMPTY_VALUE)}
            </Typography>
          </Box>
        );
      }

      case "enum": {
        const enumValue = String(rawValue);

        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {enumValue || EMPTY_VALUE}
            </Typography>
          </Box>
        );
      }

      case "boolean": {
        const boolValue = rawValue === true || rawValue === "true";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: boolValue ? "success.main" : "grey.400",
              }}
            />
            <Typography variant="body2">
              {boolValue ? t(YES) : t(NO)}
            </Typography>
          </Box>
        );
      }

      case "object":
        try {
          const objValue = parseValueSafely(rawValue);
          if (!objValue || typeof objValue !== "object") {
            return (
              <Typography variant="body2">
                {String(rawValue || EMPTY_VALUE)}
              </Typography>
            );
          }

          // Show ALL key-value pairs for objects in a single line (excluding 'id')
          const schemaKeys = Object.keys(fieldSchema.schema || {});
          const keyValuePairs = getKeyValuePairs(objValue, schemaKeys);

          return (
            <Box>
              {keyValuePairs.map((pair, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {pair}
                </Typography>
              ))}
            </Box>
          );
        } catch (e) {
          return (
            <Typography variant="body2">
              {String(rawValue || EMPTY_VALUE)}
            </Typography>
          );
        }

      case "number": {
        return (
          <Typography variant="body2">
            {String(rawValue || EMPTY_VALUE)}
          </Typography>
        );
      }

      default:
        return (
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
          >
            {String(rawValue || EMPTY_VALUE)}
          </Typography>
        );
    }
  };

  return <Box sx={{ position: "relative" }}>{renderValueByType()}</Box>;
};

export default ModalPreviewOptimization;
